import { useCallback, useEffect, useRef, useState } from 'react';
import { listFunctionalLibrary, FunctionalLibraryListResponse } from '../../api/functionalLibraryApi';
import { evaluateNearMiss, getNearMissEvaluation, getNearMissStatus, listNearMissEpisodes, NearMissEpisodePage, NearMissEvaluation } from '../../api/nearMiss';
import { formatApiError } from '../../utils/formatApiError';

interface View {
  project: string;
  evaluationId?: string;
  status: NearMissEvaluation | null;
  episodes: NearMissEpisodePage | null;
  library: FunctionalLibraryListResponse | null;
  errors: Record<string, string>;
}

export function useNearMissObservations(project: string, evaluationId?: string) {
  const scope = useRef({ project, evaluationId, generation: 0 });
  if (scope.current.project !== project || scope.current.evaluationId !== evaluationId) {
    scope.current = { project, evaluationId, generation: scope.current.generation + 1 };
  }
  const mounted = useRef(false);
  const reads = useRef(0);
  const pageReads = useRef(0);
  const pending = useRef<{ generation: number; key: string; busy: boolean } | null>(null);
  const [view, setView] = useState<View>({ project, evaluationId, status: null, episodes: null, library: null, errors: {} });
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [notice, setNotice] = useState('');
  const validScope = useCallback((generation: number) => mounted.current && scope.current.generation === generation, []);
  const empty = useCallback((): View => ({ project, evaluationId, status: null, episodes: null, library: null, errors: {} }), [project, evaluationId]);

  const refresh = useCallback(async () => {
    const generation = scope.current.generation;
    const read = ++reads.current;
    setLoading(true);
    const results = await Promise.allSettled([
      evaluationId ? getNearMissEvaluation(project, evaluationId) : getNearMissStatus(project),
      listFunctionalLibrary(project),
    ]);
    if (!validScope(generation) || reads.current !== read) return;
    setView(previous => {
      const next = previous.project === project && previous.evaluationId === evaluationId ? { ...previous, errors: { ...previous.errors } } : empty();
      const [status, library] = results;
      if (status.status === 'fulfilled') { next.status = status.value; delete next.errors.status; }
      else next.errors.status = formatApiError(status.reason, 'Stored observation results could not be loaded.');
      if (library.status === 'fulfilled') { next.library = library.value; delete next.errors.library; }
      else { next.library = null; next.errors.library = formatApiError(library.reason, 'Test ownership could not be verified from the current library.'); }
      return next;
    });
    setLoading(false);
  }, [empty, evaluationId, project, validScope]);

  const loadEpisodes = useCallback(async (offset = 0) => {
    const generation = scope.current.generation;
    const read = ++pageReads.current;
    setPageLoading(true);
    try {
      const page = await listNearMissEpisodes(project, offset, 20);
      if (!validScope(generation) || pageReads.current !== read) return;
      setView(previous => ({ ...(previous.project === project && previous.evaluationId === evaluationId ? previous : empty()), episodes: page,
        errors: Object.fromEntries(Object.entries(previous.errors).filter(([key]) => key !== 'episodes')) }));
    } catch (error) {
      if (!validScope(generation) || pageReads.current !== read) return;
      setView(previous => ({ ...previous, errors: { ...previous.errors, episodes: formatApiError(error, 'Stored episodes could not be loaded.') } }));
    } finally { if (validScope(generation) && pageReads.current === read) setPageLoading(false); }
  }, [empty, evaluationId, project, validScope]);

  useEffect(() => {
    const readCounter = reads;
    const pageCounter = pageReads;
    mounted.current = true;
    setView(empty()); setEvaluating(false); setNotice(''); pending.current = null;
    void refresh(); void loadEpisodes();
    return () => { mounted.current = false; ++readCounter.current; ++pageCounter.current; };
  }, [empty, refresh, loadEpisodes]);

  const evaluate = async (allowed: boolean) => {
    const generation = scope.current.generation;
    if (!allowed || evaluationId || (pending.current?.generation === generation && pending.current.busy)) return;
    if (pending.current?.generation !== generation) pending.current = { generation,
      key: `ui:${Date.now()}:${Math.random().toString(36).slice(2)}`, busy: false };
    const request = pending.current!;
    request.busy = true;
    ++reads.current; // A GET begun before this POST must not replace its result.
    setEvaluating(true); setNotice('');
    setView(previous => ({ ...previous, errors: Object.fromEntries(Object.entries(previous.errors).filter(([key]) => key !== 'evaluate')) }));
    let completed = false;
    try {
      const status = await evaluateNearMiss(project, request.key);
      if (!validScope(generation)) return;
      completed = true;
      setView(previous => ({ ...previous, status }));
      setNotice('Evaluation saved. No test selection was requested.');
      await Promise.all([refresh(), loadEpisodes()]);
    } catch (error) {
      if (!validScope(generation)) return;
      setView(previous => ({ ...previous, errors: { ...previous.errors,
        evaluate: `${formatApiError(error, 'Evaluation could not be confirmed.')} Retry uses the same request key to avoid duplicate evaluations. Check your project execution permission if access was denied.` } }));
    } finally {
      if (validScope(generation)) { request.busy = false; if (completed) pending.current = null; setEvaluating(false); }
    }
  };
  const resetRetry = useCallback(() => { if (!pending.current?.busy) pending.current = null; }, []);
  const current = view.project === project && view.evaluationId === evaluationId;
  return { ...(current ? view : empty()), loading: !current || loading, pageLoading,
    evaluating: current && evaluating, notice: current ? notice : '', refresh, loadEpisodes, evaluate,
    resetRetry };
}
